import * as React from 'react'

import { Drawer as DrawerPrimitive } from 'vaul'

import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '~/client/src/shared/components/shadcn-ui/drawer'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'

// Nested admin dialogs (rename, delete) MUST portal into `nestedDialogPortalRef`,
// otherwise they land at document.body — outside Radix's focus trap, which then
// steals focus back on every keystroke (rename input can't receive characters).

// 85vh (not shadcn's default 80vh) — items counter + filters bar consume vertical space.
const BOTTOM_CLASSNAME =
  'tw:data-[vaul-drawer-direction=bottom]:max-h-[85vh] tw:p-0'

interface IProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  nestedDialogPortalRef?: React.RefObject<HTMLDivElement | null>
}

// Portal-rendered surfaces that suppress the drawer's outside-press dismiss.
// MUST NOT include bare `role="dialog"` — the drawer itself carries it; matching
// it would make every ESC self-cancel and the drawer would never close.
const PORTAL_SELECTORS_THAT_BLOCK_DISMISS = [
  '[data-slot="dropdown-menu-content"]',
  '[data-slot="dropdown-menu-positioner"]',
  '[data-slot="popover-content"]',
  '[data-slot="dialog-content"]',
  '[role="alertdialog"]',
  '[role="menu"]',
].join(',')

const isPortalChildOpen = (): boolean =>
  typeof document !== 'undefined' &&
  !!document.querySelector(PORTAL_SELECTORS_THAT_BLOCK_DISMISS)

export const SheetSurface: React.FC<IProps> = ({
  open,
  onOpenChange,
  children,
  nestedDialogPortalRef,
}) => {
  // Capture focused trigger DURING RENDER (not useEffect/useLayoutEffect) —
  // both run after vaul's autoFocus pulls focus to the drawer's close X, so
  // they'd snapshot the wrong element. Render-time ref write on a false→true
  // transition runs synchronously while document.activeElement is still the trigger.
  // Safe in React 17 because we only mutate a ref, no observable state.
  const triggerRef = React.useRef<HTMLElement | null>(null)
  const prevOpenRef = React.useRef(open)
  if (!prevOpenRef.current && open && !triggerRef.current) {
    triggerRef.current = document.activeElement as HTMLElement | null
  }
  prevOpenRef.current = open

  React.useEffect(() => {
    if (open || !triggerRef.current) return
    // Defer one frame past vaul's exit animation — focusing animating elements jitters some browsers.
    const target = triggerRef.current
    triggerRef.current = null
    requestAnimationFrame(() => target.focus({ preventScroll: true }))
  }, [open])
  const handleOpenChange = (next: boolean): void => {
    // Vaul's DOM-based outside-press detector sees clicks on portal children
    // (menus, nested dialogs) as "outside" the drawer — short-circuit close.
    if (!next && isPortalChildOpen()) return
    onOpenChange(next)
  }
  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      direction="bottom"
      // Vaul's default autoFocus=false leaves focus on the trigger pill — outside Radix's
      // focus scope — so ESC/Arrow/Tab keys bypass the drawer (Radix handlers only fire
      // when focus is inside its content tree).
      autoFocus={true}
    >
      <DrawerContent className={cn(BOTTOM_CLASSNAME)}>
        <DrawerTitle className="tw:sr-only">
          {Localization.translator.selectMapView}
        </DrawerTitle>
        <DrawerPrimitive.Description className="tw:sr-only">
          {Localization.translator.mapViewsBrowseAndSelect}
        </DrawerPrimitive.Description>
        {children}
        <div ref={nestedDialogPortalRef} />
      </DrawerContent>
    </Drawer>
  )
}
